'use client'

import { useEffect, useMemo, useState, useRef } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { Search, X } from 'lucide-react'

import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import {
	useAddCampaignToGroup,
	useRemoveCampaignFromGroup,
} from '@/core/hooks/useCampaign'
import { useGroups } from '@/core/hooks/useGroup'
import { useDebounce } from '@/core/hooks/utils/useDebounce'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { Checkbox } from '@/shared/components/atoms/Checkbox'
import { Input } from '@/shared/components/atoms/Input'
import { ScrollArea } from '@/shared/components/atoms/Scroll'
import { Separator } from '@/shared/components/atoms/Separator'
import {
	BottomSheet,
	BottomSheetContent,
	BottomSheetTitle,
} from '@/shared/components/molecules/BottomSheet/BottomSheet'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'
import { ModalPaginator } from '@/shared/components/molecules/ModalPaginator/ModalPaginator'
import { useNotificationContext } from '@/shared/context/NotificationProvider'

interface Group {
	id: number
	name: string
	analytics?: {
		players: number
		radios: number
		mixes: number
	}
}

interface AssignCampaignModalProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	campaignId?: number
	campaignName?: string
	/**
	 * IDs of groups where this campaign is already assigned.
	 * Used to preselect checkboxes when reopening the modal.
	 */
	initialGroupIds?: number[]
}

export function AssignCampaignModal({
	open,
	onOpenChange,
	campaignId,
	campaignName,
	initialGroupIds,
}: AssignCampaignModalProps) {
	const { t } = useTranslation('campaigns')
	const queryClient = useQueryClient()
	const { showNotification } = useNotificationContext()
	const [searchQuery, setSearchQuery] = useState('')
	const [page, setPage] = useState(1)
	const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([])
	const [isAssigning, setIsAssigning] = useState(false)
	const [isDesktop, setIsDesktop] = useState(
		() => !window.matchMedia('(max-width: 767px)').matches
	)
	const initialSelectedGroupsRef = useRef<number[]>([])

	const debouncedSearchQuery = useDebounce(searchQuery, 500)

	useEffect(() => {
		setPage(1)
	}, [debouncedSearchQuery])

	useEffect(() => {
		const mediaQuery = window.matchMedia('(max-width: 767px)')
		const handler = (e: MediaQueryListEvent) => setIsDesktop(!e.matches)
		mediaQuery.addEventListener('change', handler)
		return () => mediaQuery.removeEventListener('change', handler)
	}, [])

	const { data: groupsResponse, isLoading: isLoadingGroups } = useGroups({
		config: useAccountConfig({
			params: {
				page,
				per_page: 20,
				query: debouncedSearchQuery || undefined,
				include: 'players,announcements,mixes',
			},
		}),
		options: {
			enabled: open,
		},
	})

	const groups = (groupsResponse?.data || []) as Group[]
	const totalPages = groupsResponse?.meta?.pagination?.total_pages ?? 1

	// Sync selected groups with the campaign's existing assignments
	// when the modal is opened, so those groups start as checked.
	useEffect(() => {
		if (open) {
			const ids =
				initialGroupIds && initialGroupIds.length > 0 ? initialGroupIds : []
			setSelectedGroupIds(ids)
			setPage(1)
			initialSelectedGroupsRef.current = ids
		} else {
			setSelectedGroupIds([])
			setSearchQuery('')
			setPage(1)
			initialSelectedGroupsRef.current = []
		}
	}, [open, initialGroupIds])

	const { mutateAsync: addCampaignToGroup } = useAddCampaignToGroup({
		config: useAccountConfig({}),
		options: {
			onSuccess: () => {
				// Refresh campaigns & groups that might be affected
				queryClient.invalidateQueries({ queryKey: ['campaigns'] })
				queryClient.invalidateQueries({ queryKey: ['group'] })
				queryClient.invalidateQueries({ queryKey: ['groups'] })
			},
		},
	})

	const { mutateAsync: removeCampaignFromGroup } = useRemoveCampaignFromGroup({
		config: useAccountConfig({}),
		options: {
			onSuccess: () => {
				// Refresh campaigns & groups that might be affected
				queryClient.invalidateQueries({ queryKey: ['campaigns'] })
				queryClient.invalidateQueries({ queryKey: ['group'] })
				queryClient.invalidateQueries({ queryKey: ['groups'] })
			},
		},
	})

	const handleToggleGroup = (groupId: number) => {
		setSelectedGroupIds((prev) =>
			prev.includes(groupId)
				? prev.filter((id) => id !== groupId)
				: [...prev, groupId]
		)
	}

	// Check if there are any changes to save
	const hasChanges = useMemo(() => {
		const initial = initialSelectedGroupsRef.current
		const added = selectedGroupIds.filter((id) => !initial.includes(id))
		const removed = initial.filter((id) => !selectedGroupIds.includes(id))
		return added.length > 0 || removed.length > 0
	}, [selectedGroupIds])

	const handleAssign = async () => {
		if (!campaignId) return

		setIsAssigning(true)

		try {
			const initialGroups = initialSelectedGroupsRef.current

			// Find groups to add (newly selected)
			const groupsToAdd = selectedGroupIds.filter(
				(groupId) => !initialGroups.includes(groupId)
			)

			// Find groups to remove (deselected)
			const groupsToRemove = initialGroups.filter(
				(groupId) => !selectedGroupIds.includes(groupId)
			)

			// Execute add operations
			const addPromises = groupsToAdd.map((groupId) =>
				addCampaignToGroup({
					groupId,
					data: { announcements: [campaignId] },
				})
			)

			// Execute remove operations
			const removePromises = groupsToRemove.map((groupId) =>
				removeCampaignFromGroup({
					groupId,
					data: { announcements: [campaignId] },
				})
			)

			await Promise.all([...addPromises, ...removePromises])

			const totalChanges = groupsToAdd.length + groupsToRemove.length

			if (totalChanges > 0) {
				// Show success notification
				const addedText =
					groupsToAdd.length > 0
						? `added to ${groupsToAdd.length} group${groupsToAdd.length > 1 ? 's' : ''}`
						: ''
				const removedText =
					groupsToRemove.length > 0
						? `removed from ${groupsToRemove.length} group${groupsToRemove.length > 1 ? 's' : ''}`
						: ''
				const description = [addedText, removedText].filter(Boolean).join(', ')

				showNotification({
					type: 'success',
					message: t('modals.assign.success'),
					description: `Campaign "${campaignName}" was ${description}`,
				})
			}

			// Reset state and close modal after successful assignment
			setSelectedGroupIds([])
			setSearchQuery('')
			setPage(1)
			initialSelectedGroupsRef.current = []
			onOpenChange(false)
		} catch (error) {
			console.error('Error assigning campaign to group:', error)
			showNotification({
				type: 'error',
				message: t('modals.assign.error'),
				description: t('modals.assign.errorDesc'),
			})
		} finally {
			setIsAssigning(false)
		}
	}

	const handleCancel = () => {
		setSelectedGroupIds([])
		setSearchQuery('')
		setPage(1)
		initialSelectedGroupsRef.current = []
		onOpenChange(false)
	}

	const isLoading = isLoadingGroups

	const content = (
		<>
			<div className="px-5 pt-2 pb-3 flex items-center justify-between">
				{isDesktop ? (
					<h2 className="text-xl font-semibold">{t('modals.assign.title')}</h2>
				) : (
					<BottomSheetTitle className="text-xl font-semibold">
						{t('modals.assign.title')}
					</BottomSheetTitle>
				)}
				{!isDesktop && (
					<Button
						type="button"
						variant="ghost"
						size="icon"
						onClick={handleCancel}
						className="h-9 w-9 rounded-full"
					>
						<X className="w-5 h-5" />
					</Button>
				)}
			</div>

			<Separator />

			<div className="px-5 py-3 flex flex-col flex-1 min-h-0 overflow-hidden">
				{campaignName && (
					<p className="text-sm text-muted-foreground flex-none mb-3">
						{t('modals.assign.assigningLabel')}{' '}
						<span className="font-medium text-foreground">{campaignName}</span>
					</p>
				)}

				<div className="relative flex-none mb-3">
					<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t('modals.assign.searchPlaceholder')}
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						className="pl-10"
					/>
				</div>

				<ScrollArea className="h-[calc(100dvh-280px)] sm:h-[calc(90vh-280px)] border rounded-md">
					{isLoading ? (
						<div className="flex items-center justify-center h-full text-muted-foreground text-sm">
							{t('modals.assign.loading')}
						</div>
					) : groups.length === 0 ? (
						<div className="flex items-center justify-center h-full text-muted-foreground text-sm">
							{t('modals.assign.noGroups')}
						</div>
					) : (
						<div className="divide-y">
							{groups.map((group) => {
								const analytics = group.analytics
								const analyticsText = analytics
									? [
											analytics.players > 0 &&
												`${analytics.players} ${t(
													`modals.assign.analytics.${
														analytics.players !== 1 ? 'players' : 'player'
													}`
												)}`,
											analytics.radios > 0 &&
												`${analytics.radios} ${t(
													`modals.assign.analytics.${
														analytics.radios !== 1 ? 'radios' : 'radio'
													}`
												)}`,
											analytics.mixes > 0 &&
												`${analytics.mixes} ${t(
													`modals.assign.analytics.${
														analytics.mixes !== 1 ? 'mixes' : 'mix'
													}`
												)}`,
										]
											.filter(Boolean)
											.join(', ')
									: ''

								return (
									<div
										key={`group-${group.id}`}
										className="flex items-center justify-between px-4 py-4 hover:bg-accent/50 cursor-pointer"
										onClick={() => handleToggleGroup(group.id)}
									>
										<div className="flex-1 pr-4 flex flex-col justify-center">
											<p
												className={`text-sm font-medium ${
													analyticsText ? 'mb-1' : ''
												}`}
											>
												{group.name}
											</p>
											{analyticsText && (
												<p className="text-xs text-muted-foreground">
													{analyticsText}
												</p>
											)}
										</div>
										<Checkbox
											checked={selectedGroupIds.includes(group.id)}
											onCheckedChange={() => handleToggleGroup(group.id)}
											onClick={(e) => e.stopPropagation()}
										/>
									</div>
								)
							})}
						</div>
					)}
				</ScrollArea>
				<ModalPaginator
					currentPage={page}
					totalPages={totalPages}
					onPageChange={setPage}
				/>
			</div>
		</>
	)

	if (isDesktop) {
		return (
			<Dialog open={open} onOpenChange={onOpenChange}>
				<DialogContent className="max-w-2xl p-0 gap-0 flex flex-col max-h-[90vh]">
					<DialogHeader className="sr-only">
						<DialogTitle>{t('modals.assign.title')}</DialogTitle>
					</DialogHeader>
					{content}
					<Separator />
					<DialogFooter className="px-6 py-4">
						<Button
							type="button"
							variant="outline"
							onClick={handleCancel}
							className="h-9 rounded-full px-6"
							disabled={isAssigning}
						>
							{t('modals.assign.cancel')}
						</Button>
						<Button
							type="button"
							onClick={handleAssign}
							disabled={!hasChanges || isAssigning}
							className="h-9 rounded-full px-6"
						>
							{isAssigning
								? t('modals.assign.assigning')
								: t('modals.assign.assign')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		)
	}

	return (
		<BottomSheet open={open} onOpenChange={onOpenChange}>
			<BottomSheetContent
				variant="card"
				className="border-border h-[86vh] max-h-[92vh] px-0 pb-4"
				aria-describedby={undefined}
			>
				{content}
				<div className="px-5 pt-2">
					<Button
						type="button"
						onClick={handleAssign}
						disabled={!hasChanges || isAssigning}
						className="h-12 rounded-full w-full"
					>
						{isAssigning
							? t('modals.assign.assigning')
							: t('modals.assign.assign')}
					</Button>
				</div>
			</BottomSheetContent>
		</BottomSheet>
	)
}
